import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { DarkModeOutlined as DarkIcon, LightModeOutlined as LightIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '../layout/LanguageToggle';
import { BrandMark } from '../layout/BrandMark';
import { useColorMode } from '../../theme/ColorModeProvider';

interface AuthSplitLayoutProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  headline?: string;
  tagline?: string;
}

// Faint dashed running route behind the brand copy; purely decorative.
const RouteDecoration = () => (
  <Box
    component="svg"
    viewBox="0 0 600 800"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden
    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', opacity: 0.22 }}
  >
    <path
      d="M-20 690 C 120 640, 160 520, 280 540 S 470 640, 520 500 S 430 300, 540 220 S 640 120, 620 60"
      fill="none"
      stroke="#fff"
      strokeWidth="3"
      strokeLinecap="round"
      strokeDasharray="2 14"
    />
    <circle cx="520" cy="500" r="7" fill="#fff" />
    <circle cx="520" cy="500" r="18" fill="none" stroke="#fff" strokeWidth="2" />
  </Box>
);

const BrandPanel = ({ headline, tagline }: { headline: string; tagline: string }) => {
  const { t } = useTranslation('auth');
  return (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      background: 'var(--grad)',
      color: '#fff',
      p: { md: 5, lg: 7 },
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      justifyContent: 'space-between',
      gap: 5,
    }}
  >
    {/* Soft light blooms give the flat gradient some depth. */}
    <Box aria-hidden sx={{ position: 'absolute', width: 420, height: 420, top: -140, right: -120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.28), transparent 65%)' }} />
    <Box aria-hidden sx={{ position: 'absolute', width: 360, height: 360, bottom: -160, left: -100, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,.18), transparent 65%)' }} />
    <RouteDecoration />

    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <BrandMark size={36} />
      <Box component="span" sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '.02em' }}>URACE</Box>
    </Box>

    <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 460 }}>
      <Box sx={{ fontSize: { md: 36, lg: 44 }, lineHeight: 1.08, fontWeight: 600, letterSpacing: '-0.035em', textWrap: 'balance' }}>
        {headline}
      </Box>
      <Box sx={{ fontSize: 16, lineHeight: 1.55, opacity: 0.88, maxWidth: 400 }}>{tagline}</Box>
    </Box>

    <Box sx={{ position: 'relative', fontSize: 12, opacity: 0.7 }}>{t('layout.worksWithStrava')}</Box>
  </Box>
  );
};

// Full-viewport shell for pages rendered outside the app Layout: gradient brand panel
// beside the form on desktop; on phones only a compact logo row sits above the form.
export const AuthSplitLayout = ({
  title,
  subtitle,
  children,
  headline,
  tagline,
}: AuthSplitLayoutProps) => {
  const { t } = useTranslation('auth');
  const { mode, toggleMode } = useColorMode();

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 1fr)' },
        backgroundColor: 'var(--card)',
        color: 'var(--fg)',
      }}
    >
      <BrandPanel headline={headline ?? t('layout.headline')} tagline={tagline ?? t('layout.tagline')} />

      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', px: { xs: 2.5, sm: 4 }, py: { xs: 2.5, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1.25 }}>
            <BrandMark size={32} />
            <Box component="span" sx={{ fontWeight: 700, fontSize: 16, letterSpacing: '.02em' }}>URACE</Box>
          </Box>
          <Box sx={{ ml: 'auto', display: 'flex' }}>
            <LanguageToggle size={36} />
          </Box>
          <Tooltip title={mode === 'dark' ? t('common:theme.light') : t('common:theme.dark')}>
            <IconButton
              onClick={toggleMode}
              aria-label={t('common:theme.toggle')}
              sx={{ width: 36, height: 36, border: '1px solid var(--border)', borderRadius: '9px', color: 'var(--fg)' }}
            >
              {mode === 'dark' ? <LightIcon sx={{ fontSize: 18 }} /> : <DarkIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: { xs: 4, md: 6 } }}>
          <Box sx={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box component="h1" sx={{ m: 0, fontSize: { xs: 26, sm: 28 }, fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.15 }}>{title}</Box>
              {subtitle && <Box sx={{ fontSize: 14, lineHeight: 1.5, color: 'var(--muted)' }}>{subtitle}</Box>}
            </Box>
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Centered "label + link" footer line used under the auth forms.
export const authFooterSx = { fontSize: 14, color: 'var(--muted)', textAlign: 'center' as const };

// Full-screen spinner shown while auth checks run.
export const AuthLoading = () => {
  const { t } = useTranslation();
  return (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: 14 }}>
    {t('common:status.loading')}
  </Box>
  );
};
